import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import api from "../api";
import { useAuth } from "../AuthContext";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Switch } from "../components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Users, Sparkles, CalendarDays, Handshake, Trash2, Plus, TrendingUp, Wallet, Boxes, UserCog, Pencil, CalendarClock, Building2, ShieldCheck, FileDown, Image as ImageIcon, Cake, Palette, MessageCircle } from "lucide-react";
import FinanceTab from "./admin/FinanceTab";
import MaterialsTab from "./admin/MaterialsTab";
import StaffTab from "./admin/StaffTab";
import PlansTab from "./admin/PlansTab";
import BusinessPartnersTab from "./admin/BusinessPartnersTab";
import SubAdminsTab from "./admin/SubAdminsTab";
import GalleryTab from "./admin/GalleryTab";
import BirthdaysCard from "./admin/BirthdaysCard";
import LoyaltyCard from "./admin/LoyaltyCard";
import RemindersCard from "./admin/RemindersCard";
import ThemeTab from "./admin/ThemeTab";
import PartnersTab from "./admin/PartnersTab";
import ComplaintsTab from "./admin/ComplaintsTab";
import PartnerApplicationsTab from "./admin/PartnerApplicationsTab";
import AlertsBanner from "../AlertsBanner";
import AppointmentHistoryCleanup from "./admin/AppointmentHistoryCleanup";
import ScheduleBlocksCard from "./admin/ScheduleBlocksCard";
import WaitlistCard from "./admin/WaitlistCard";
import { ImageUpload } from "../components/ImageUpload";
import { ClientGallery } from "../components/ClientGallery";
import { mediaUrl } from "../media";
import { generateAgendaReport } from "../pdfReports";

const emptyService = { title:"", description:"", duration_min:60, price:0, category:"Esmaltação", active:true, image_url:"" };
const emptyPartner = { name:"", category:"", benefit:"", code:"" };

const ROLE_LABEL = {
  admin: "Super Admin",
  admin_admin: "Admin Administrativo",
  admin_caixa: "Admin Caixa da Loja",
};

export default function AdminPage() {
  const { user } = useAuth();
  const role = user?.role;
  const isSuper = role === "admin";
  const isOperational = role === "admin" || role === "admin_admin";
  const isCashier = role === "admin" || role === "admin_caixa";

  const [stats, setStats] = useState(null);
  const [apts, setApts] = useState([]);
  const [clients, setClients] = useState([]);
  const [services, setServices] = useState([]);
  const [partners, setPartners] = useState([]);
  const [newSvc, setNewSvc] = useState(emptyService);
  const [newPt, setNewPt] = useState(emptyPartner);
  const [editSvc, setEditSvc] = useState(null);
  const [galleryUid, setGalleryUid] = useState(null);
  const openWhats = (c) => {
    const phone = String(c.phone || "").replace(/\D/g, "");
    if (!phone) { toast.error("Cliente sem telefone cadastrado"); return; }
    const msg = `Olá, ${(c.name || "").split(" ")[0]}! Aqui é do Arya Studio K&C.`;
    window.open(`https://wa.me/${phone.startsWith("55") ? phone : "55" + phone}?text=${encodeURIComponent(msg)}`, "_blank");
  };
  const clearHistory = async (c) => {
    if (!window.confirm(`Zerar o histórico de ${c.name}? Visitas e pontos voltam a zero e os atendimentos passados são apagados. Agendamentos futuros continuam.`)) return;
    try {
      const { data } = await api.delete(`/admin/clients/${c.user_id}/history`);
      toast.success(`Histórico zerado (${data.archived} atendimento(s))`); loadAll();
    } catch (e) { toast.error(e.response?.data?.detail || "Falha ao zerar histórico"); }
  };
  const clearAllHistory = async () => {
    if (!window.confirm("Zerar o histórico de TODAS as clientes? Visitas e pontos voltam a zero e os atendimentos passados são apagados. Agendamentos futuros continuam.")) return;
    if (!window.confirm("Tem certeza? Esta ação vale para todas as clientes.")) return;
    try {
      const { data } = await api.delete("/admin/clients-history");
      toast.success(`Histórico de todas zerado (${data.archived} atendimento(s))`); loadAll();
    } catch (e) { toast.error(e.response?.data?.detail || "Falha ao zerar histórico"); }
  };
  const [agendaDate, setAgendaDate] = useState(new Date().toISOString().slice(0,10));

  const exportAgenda = async () => {
    try {
      const { data } = await api.get("/admin/agenda", { params: { date: agendaDate } });
      await generateAgendaReport({ date: agendaDate, appointments: data });
    } catch { toast.error("Falha ao gerar agenda"); }
  };

  const loadAll = () => {
    if (isOperational) {
      api.get("/admin/stats").then(r=>setStats(r.data)).catch(()=>{});
      api.get("/admin/appointments").then(r=>setApts(r.data)).catch(()=>{});
      api.get("/admin/clients").then(r=>setClients(r.data)).catch(()=>{});
      api.get("/admin/services").then(r=>setServices(r.data)).catch(()=>{});
      api.get("/partnerships").then(r=>setPartners(r.data)).catch(()=>{});
    }
  };
  useEffect(loadAll, [isOperational]);

  const toggleSvc = async (id) => { try { await api.patch(`/admin/services/${id}/toggle`); toast.success("Visibilidade atualizada"); loadAll(); } catch { toast.error("Falha"); } };
  const delSvc = async (id) => { if (!window.confirm("Excluir este serviço?")) return; await api.delete(`/admin/services/${id}`); toast.success("Excluído"); loadAll(); };
  const createSvc = async () => { if (!newSvc.title) return toast.error("Título obrigatório"); await api.post("/admin/services", newSvc); toast.success("Serviço criado"); setNewSvc(emptyService); loadAll(); };
  const saveEditSvc = async () => {
    const { service_id, ...body } = editSvc;
    body.price = Number(body.price)||0; body.duration_min = Number(body.duration_min)||0;
    try { await api.patch(`/admin/services/${service_id}`, body); toast.success("Serviço atualizado"); setEditSvc(null); loadAll(); }
    catch { toast.error("Falha ao salvar"); }
  };
  const completeApt = async (id) => { try { await api.post(`/admin/appointments/${id}/complete`); toast.success("Concluído"); loadAll(); } catch (e) { toast.error(e.response?.data?.detail || "Falha"); } };
  const adminCancel = async (id) => { try { await api.post(`/appointments/${id}/cancel`); toast.success("Cancelado"); loadAll(); } catch (e) { toast.error(e.response?.data?.detail || "Falha"); } };
  const createPt = async () => { if (!newPt.name) return toast.error("Nome obrigatório"); await api.post("/admin/partnerships", newPt); toast.success("Parceria criada"); setNewPt(emptyPartner); loadAll(); };
  const delPt = async (id) => { if (!window.confirm("Excluir parceria?")) return; await api.delete(`/admin/partnerships/${id}`); loadAll(); };

  // Default tab based on role
  const defaultTab = isOperational ? "appointments" : "finance";

  return (
    <div className="max-w-7xl mx-auto px-6 py-10">
      <div className="mb-8 flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="tag-sun mb-2 inline-block">{ROLE_LABEL[role] || "Admin"}</div>
          <h1 className="font-serif text-4xl">Central Arya Studio</h1>
          <p className="text-sm text-[color:var(--ink-500)] mt-1">Gerencie agenda, clientes, especialidades e parcerias.</p>
        </div>
        {isOperational && (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {[
              { label:"Clientes", value: stats?.total_clients ?? 0, icon: <Users size={14}/> },
              { label:"Agendamentos", value: stats?.total_appointments ?? 0, icon: <CalendarDays size={14}/> },
              { label:"Próximos", value: stats?.upcoming ?? 0, icon: <TrendingUp size={14}/> },
              { label:"Concluídos", value: stats?.completed ?? 0, icon: <Sparkles size={14}/> },
            ].map((s,i)=>(
              <div key={i} className="card-arya p-4 min-w-[130px]">
                <div className="flex items-center gap-2 text-[color:var(--sun-700)] text-[10px] tracking-widest font-display">{s.icon} {s.label.toUpperCase()}</div>
                <p className="font-serif text-3xl mt-1">{s.value}</p>
              </div>
            ))}
          </div>
        )}
      </div>

      <Tabs defaultValue={defaultTab} className="w-full">
        {(isOperational || isCashier) && <AlertsBanner />}
        <TabsList className="bg-[var(--cream-100)] p-1 rounded-full flex-wrap h-auto">
          {isOperational && <TabsTrigger value="appointments" data-testid="tab-appointments">Agendamentos</TabsTrigger>}
          {isOperational && <TabsTrigger value="clients" data-testid="tab-clients">Clientes</TabsTrigger>}
          {isOperational && <TabsTrigger value="plans" data-testid="tab-plans"><CalendarClock size={14} className="mr-1"/>Planos</TabsTrigger>}
          {isOperational && <TabsTrigger value="services" data-testid="tab-services">Especialidades</TabsTrigger>}
          {isOperational && <TabsTrigger value="partners" data-testid="tab-partners">Cupons</TabsTrigger>}
          {isOperational && <TabsTrigger value="empresas" data-testid="tab-business"><Building2 size={14} className="mr-1"/>Empresas</TabsTrigger>}
          {isOperational && <TabsTrigger value="staff" data-testid="tab-staff"><UserCog size={14} className="mr-1"/>Equipe</TabsTrigger>}
          {isOperational && <TabsTrigger value="gallery" data-testid="tab-gallery"><ImageIcon size={14} className="mr-1"/>Galeria</TabsTrigger>}
          {isOperational && <TabsTrigger value="complaints" data-testid="tab-complaints">Reclamações</TabsTrigger>}
          {isOperational && <TabsTrigger value="applications" data-testid="tab-applications">Candidaturas</TabsTrigger>}
          {isCashier && <TabsTrigger value="finance" data-testid="tab-finance"><Wallet size={14} className="mr-1"/>Financeiro</TabsTrigger>}
          {isCashier && <TabsTrigger value="materials" data-testid="tab-materials"><Boxes size={14} className="mr-1"/>Materiais</TabsTrigger>}
          {isSuper && <TabsTrigger value="subadmins" data-testid="tab-subadmins"><ShieldCheck size={14} className="mr-1"/>Sub-admins</TabsTrigger>}
          {isSuper && <TabsTrigger value="theme" data-testid="tab-theme"><Palette size={14} className="mr-1"/>Aparência</TabsTrigger>}
        </TabsList>

        {isOperational && <>
        <TabsContent value="appointments" className="mt-6">
          <div className="mb-4"><RemindersCard /></div>
          <WaitlistCard />
          <ScheduleBlocksCard />
          <AppointmentHistoryCleanup onDone={loadAll} />
          <div className="card-arya p-4 mb-4 flex flex-wrap items-center gap-3">
            <div>
              <label className="text-[10px] tracking-widest font-display text-[color:var(--sun-700)]">AGENDA DO DIA</label>
              <input type="date" value={agendaDate} onChange={e=>setAgendaDate(e.target.value)}
                     className="block mt-1 rounded-xl border border-[var(--line)] bg-white px-3 py-2 text-sm" data-testid="agenda-date"/>
            </div>
            <button onClick={exportAgenda} className="btn-ghost inline-flex items-center gap-2" data-testid="btn-pdf-agenda">
              <FileDown size={14}/> Baixar PDF da agenda
            </button>
            <p className="text-xs text-[color:var(--ink-500)] flex-1">Exporte a agenda diária para imprimir e deixar no estúdio.</p>
          </div>
          <div className="card-arya overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-[var(--cream-100)] text-left text-xs tracking-widest font-display">
                  <tr><th className="p-4">CLIENTE</th><th className="p-4">SERVIÇO</th><th className="p-4">DATA</th><th className="p-4">PGTO</th><th className="p-4">STATUS</th><th className="p-4">AÇÕES</th></tr>
                </thead>
                <tbody className="divide-y divide-[var(--line)]">
                  {apts.map(a => (
                    <tr key={a.appointment_id} data-testid={`admin-apt-${a.appointment_id}`}>
                      <td className="p-4">
                        <div className="font-display font-semibold">{a.user_name}</div>
                        <div className="text-xs text-[color:var(--ink-500)]">{a.user_email}</div>
                      </td>
                      <td className="p-4">{a.service_title}<br/><span className="text-xs text-[color:var(--ink-500)]">R$ {a.price.toFixed(2).replace('.',',')} · {a.duration_min}min</span></td>
                      <td className="p-4">{new Date(a.start_at).toLocaleString("pt-BR", { dateStyle:"short", timeStyle:"short" })}</td>
                      <td className="p-4 text-xs">{a.payment_method}</td>
                      <td className="p-4"><span className="tag-sun">{a.status}</span></td>
                      <td className="p-4 flex gap-2">
                        {a.status === "confirmed" && <>
                          <button onClick={()=>completeApt(a.appointment_id)} className="btn-gold !py-1 !px-3 text-xs" data-testid={`btn-complete-${a.appointment_id}`}>Concluir</button>
                          <button onClick={()=>adminCancel(a.appointment_id)} className="btn-ghost !py-1 !px-3 text-xs" data-testid={`btn-admin-cancel-${a.appointment_id}`}>Cancelar</button>
                        </>}
                      </td>
                    </tr>
                  ))}
                  {apts.length===0 && <tr><td colSpan="6" className="p-8 text-center text-[color:var(--ink-500)]">Sem agendamentos.</td></tr>}
                </tbody>
              </table>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="clients" className="mt-6">
          <div className="grid lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 card-arya overflow-hidden">
            <div className="flex justify-end p-3 border-b border-[var(--line)]">
              <button onClick={clearAllHistory} className="btn-ghost !py-1 !px-3 text-xs !text-[color:var(--danger)]" data-testid="btn-clear-all-history">Zerar histórico de todas</button>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-[var(--cream-100)] text-left text-xs tracking-widest font-display">
                  <tr><th className="p-3">NOME</th><th className="p-3">EMAIL</th><th className="p-3">TELEFONE</th><th className="p-3">VISITAS</th><th className="p-3">PTS</th><th className="p-3 hidden xl:table-cell">CÓDIGO</th><th className="p-3">AÇÕES</th></tr>
                </thead>
                <tbody className="divide-y divide-[var(--line)]">
                  {clients.map(c => (
                    <tr key={c.user_id} data-testid={`admin-client-${c.user_id}`}>
                      <td className="p-3 font-display font-semibold">{c.name}</td>
                      <td className="p-3 max-w-[160px] truncate" title={c.email}>{c.email}</td>
                      <td className="p-3 whitespace-nowrap">{c.phone || "—"}</td>
                      <td className="p-3"><span className="font-serif text-lg text-[color:var(--sun-700)]">{c.visit_count || 0}</span></td>
                      <td className="p-3">{c.referral_points || 0}</td>
                      <td className="p-3 font-mono text-xs hidden xl:table-cell">{c.referral_code}</td>
                      <td className="p-3"><div className="flex gap-1">
                        <button onClick={()=>setGalleryUid(c.user_id)} className="btn-ghost !py-1 !px-2 text-xs inline-flex items-center gap-1" data-testid={`client-photos-${c.user_id}`} title="Fotos"><ImageIcon size={12}/></button>
                        <button onClick={()=>openWhats(c)} className="btn-ghost !py-1 !px-2 text-xs inline-flex items-center gap-1" data-testid={`client-whats-${c.user_id}`} title="WhatsApp"><MessageCircle size={12}/></button>
                        <button onClick={()=>clearHistory(c)} className="btn-ghost !py-1 !px-2 text-xs !text-[color:var(--danger)]" data-testid={`client-clear-${c.user_id}`} title="Zerar histórico"><Trash2 size={12}/></button>
                      </div></td>
                    </tr>
                  ))}
                  {clients.length===0 && <tr><td colSpan="7" className="p-8 text-center text-[color:var(--ink-500)]">Sem clientes.</td></tr>}
                </tbody>
              </table>
            </div>
          </div>
          <BirthdaysCard />
          <LoyaltyCard />
          </div>
        </TabsContent>

        <TabsContent value="plans" className="mt-6"><PlansTab /></TabsContent>

        <TabsContent value="services" className="mt-6 grid lg:grid-cols-3 gap-6">
          <div className="card-arya p-6 lg:col-span-2">
            <p className="text-xs tracking-widest font-display text-[color:var(--sun-700)] mb-3">SELECIONE OCULTAMENTE OS SERVIÇOS · EDITE NOME E VALORES</p>
            <ul className="divide-y divide-[var(--line)]">
              {services.map(s => (
                <li key={s.service_id} className="py-4 flex flex-wrap items-center justify-between gap-3" data-testid={`admin-svc-${s.service_id}`}>
                  {s.image_url && <img src={mediaUrl(s.image_url)} alt="" className="w-14 h-14 rounded-xl object-cover" data-testid={`admin-svc-img-${s.service_id}`}/>}
                  <div className="flex-1 min-w-[200px]">
                    <p className="font-display font-semibold">{s.title}</p>
                    <p className="text-xs text-[color:var(--ink-500)]">{s.category} · {s.duration_min} min · R$ {s.price.toFixed(2).replace('.',',')}</p>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="text-xs text-[color:var(--ink-500)]">{s.active ? "Visível" : "Oculto"}</span>
                    <Switch checked={s.active} onCheckedChange={()=>toggleSvc(s.service_id)} data-testid={`toggle-svc-${s.service_id}`}/>
                    <button onClick={()=>setEditSvc({...s})} className="btn-ghost !py-1 !px-3 text-xs inline-flex items-center gap-1" data-testid={`edit-svc-${s.service_id}`}><Pencil size={12}/> EDITAR NOME E VALORES</button>
                    <button onClick={()=>delSvc(s.service_id)} className="p-2 text-[color:var(--danger)]" data-testid={`del-svc-${s.service_id}`}><Trash2 size={16}/></button>
                  </div>
                </li>
              ))}
            </ul>
          </div>
          <div className="card-arya p-6">
            <h3 className="font-serif text-xl mb-3 flex items-center gap-2"><Plus size={16}/> Nova especialidade</h3>
            <div className="space-y-2">
              <input placeholder="Título" value={newSvc.title} onChange={e=>setNewSvc({...newSvc, title:e.target.value})} className="w-full rounded-xl border border-[var(--line)] bg-white px-3 py-2 text-sm" data-testid="input-new-svc-title"/>
              <input placeholder="Categoria" value={newSvc.category} onChange={e=>setNewSvc({...newSvc, category:e.target.value})} className="w-full rounded-xl border border-[var(--line)] bg-white px-3 py-2 text-sm"/>
              <textarea placeholder="Descrição" value={newSvc.description} onChange={e=>setNewSvc({...newSvc, description:e.target.value})} className="w-full rounded-xl border border-[var(--line)] bg-white px-3 py-2 text-sm" rows="2"/>
              <div className="grid grid-cols-2 gap-2">
                <input type="number" placeholder="Duração (min)" value={newSvc.duration_min} onChange={e=>setNewSvc({...newSvc, duration_min:Number(e.target.value)})} className="rounded-xl border border-[var(--line)] bg-white px-3 py-2 text-sm"/>
                <input type="number" step="0.01" placeholder="Preço" value={newSvc.price} onChange={e=>setNewSvc({...newSvc, price:Number(e.target.value)})} className="rounded-xl border border-[var(--line)] bg-white px-3 py-2 text-sm"/>
              </div>
              <ImageUpload value={newSvc.image_url} onChange={(u)=>setNewSvc({...newSvc, image_url:u})} testid="new-svc-image"/>
              <label className="flex items-center gap-2 text-sm">
                <Switch checked={newSvc.active} onCheckedChange={(v)=>setNewSvc({...newSvc, active:v})}/>
                Visível ao criar
              </label>
              <button onClick={createSvc} className="btn-gold w-full" data-testid="btn-create-svc">Adicionar</button>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="partners" className="mt-6"><PartnersTab /></TabsContent>

        <TabsContent value="empresas" className="mt-6"><BusinessPartnersTab /></TabsContent>
        <TabsContent value="staff" className="mt-6"><StaffTab /></TabsContent>
        <TabsContent value="gallery" className="mt-6"><GalleryTab /></TabsContent>
        <TabsContent value="complaints" className="mt-6"><ComplaintsTab /></TabsContent>
        <TabsContent value="applications" className="mt-6"><PartnerApplicationsTab /></TabsContent>
        </>}

        {isCashier && <>
          <TabsContent value="finance" className="mt-6"><FinanceTab /></TabsContent>
          <TabsContent value="materials" className="mt-6"><MaterialsTab /></TabsContent>
        </>}

        {isSuper && <TabsContent value="subadmins" className="mt-6"><SubAdminsTab /></TabsContent>}
        {isSuper && <TabsContent value="theme" className="mt-6"><ThemeTab /></TabsContent>}
      </Tabs>

      {/* Edit service dialog */}
      <Dialog open={!!editSvc} onOpenChange={(o)=> !o && setEditSvc(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle className="font-serif text-2xl">Editar nome e valores</DialogTitle></DialogHeader>
          {editSvc && (
            <div className="space-y-2">
              <label className="text-[10px] tracking-widest text-[color:var(--sun-700)] font-display">NOME</label>
              <input value={editSvc.title} onChange={e=>setEditSvc({...editSvc, title:e.target.value})} className="w-full rounded-xl border border-[var(--line)] bg-white px-3 py-2 text-sm" data-testid="edit-svc-title"/>
              <label className="text-[10px] tracking-widest text-[color:var(--sun-700)] font-display">CATEGORIA</label>
              <input value={editSvc.category} onChange={e=>setEditSvc({...editSvc, category:e.target.value})} className="w-full rounded-xl border border-[var(--line)] bg-white px-3 py-2 text-sm"/>
              <label className="text-[10px] tracking-widest text-[color:var(--sun-700)] font-display">DESCRIÇÃO</label>
              <textarea rows="2" value={editSvc.description||""} onChange={e=>setEditSvc({...editSvc, description:e.target.value})} className="w-full rounded-xl border border-[var(--line)] bg-white px-3 py-2 text-sm"/>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="text-[10px] tracking-widest text-[color:var(--sun-700)] font-display">DURAÇÃO (min)</label>
                  <input type="number" value={editSvc.duration_min} onChange={e=>setEditSvc({...editSvc, duration_min:e.target.value})} className="w-full rounded-xl border border-[var(--line)] bg-white px-3 py-2 text-sm" data-testid="edit-svc-duration"/>
                </div>
                <div>
                  <label className="text-[10px] tracking-widest text-[color:var(--sun-700)] font-display">PREÇO (R$)</label>
                  <input type="number" step="0.01" value={editSvc.price} onChange={e=>setEditSvc({...editSvc, price:e.target.value})} className="w-full rounded-xl border border-[var(--line)] bg-white px-3 py-2 text-sm" data-testid="edit-svc-price"/>
                </div>
              </div>
              <ImageUpload value={editSvc.image_url} onChange={(u)=>setEditSvc({...editSvc, image_url:u})} testid="edit-svc-image"/>
              <label className="flex items-center gap-2 text-sm pt-1">
                <Switch checked={editSvc.active} onCheckedChange={(v)=>setEditSvc({...editSvc, active:v})}/>
                Visível para clientes
              </label>
            </div>
          )}
          <DialogFooter>
            <button onClick={()=>setEditSvc(null)} className="btn-ghost">Cancelar</button>
            <button onClick={saveEditSvc} className="btn-gold" data-testid="btn-save-edit-svc">Salvar</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!galleryUid} onOpenChange={(o)=> !o && setGalleryUid(null)}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="client-gallery-dialog">
          <DialogHeader><DialogTitle className="font-serif text-2xl">{clients.find(c=>c.user_id===galleryUid)?.name}</DialogTitle></DialogHeader>
          {galleryUid && <ClientGallery uid={galleryUid}/>}
        </DialogContent>
      </Dialog>
    </div>
  );
}
